/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FormEvent, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Titulo, Header } from './styles';
import { InputForm } from '../input';
import { FormGroup } from '../group';
import { Button } from '../button';
import { RootState } from '../../../../../../infrastructure/redux/store';
import { TextAreaForm } from '../field';
import { InputToggle } from '../toggle';
import { Scroll } from '../../layout_components/scroll';
import { Loading } from '../../form_components/loading';
import { parser } from '../../../utils/parsers/parser';
import { InputSelect, SelectItem } from '../select';
import { FormContainer } from '../container/styles';

interface Props<T> {
  form_name: string;
  is_edit?: boolean;
  fields: FormField[];
  formName?: string;
  submitText?: string;
  loading?: boolean;
  not_reset_on_submit?: boolean;
  onSubmit: (form_data: T) => void;
  onCancel?: () => void;
}

export type FormFieldTypes =
  | 'text'
  | 'tel'
  | 'number'
  | 'checkbox'
  | 'radio'
  | 'date'
  | 'time'
  | 'email'
  | 'url'
  | 'group'
  | 'password';

interface FormField {
  name: string;
  id?: string;
  label?: string;
  type: FormFieldTypes;
  defaultValue?: string | number;
  value?: string | number;
  defaultChecked?: boolean;
  masks?: string[];
  placeholder?: string;
  items?: SelectItem[];
  required?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  sub_fields?: FormField[];
  maxLength?: number;
  component:
    | typeof InputForm
    | typeof TextAreaForm
    | typeof FormGroup
    | typeof InputToggle
    | typeof InputSelect;
  onChange?: (event: FormEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
}

interface FormValue {
  key: string;
  type: FormFieldTypes;
}

const Form = <T,>(props: Props<T>) => {
  const { theme } = useSelector((state: RootState) => state.theme);
  const [formValues, setFormValues] = useState<FormValue[]>([]);

  const handle_submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form_data = {} as any;

    for (const value of formValues) {
      const element = e.currentTarget.elements.namedItem(value.key) as HTMLInputElement;
      form_data[value.key] = parser(element.value, value.type);
    }

    props.onSubmit({
      ...form_data,
    });

    if (!props.not_reset_on_submit) {
      e.currentTarget.reset();
    }
  };

  useEffect(() => {
    const keys = new Array<FormValue>();

    props.fields.forEach((field) => {
      if (field.type !== 'group') {
        keys.push({ key: field.name, type: field.type });
      } else {
        field.sub_fields?.forEach((sub_field) => {
          keys.push({ key: sub_field.name, type: sub_field.type });
        });
      }
    });

    setFormValues(keys);
  }, [props.fields]);

  return (
    <FormContainer onSubmit={handle_submit}>
      <Header>
        <Titulo>
          {props.formName || (props.is_edit ? 'Editar' : 'Cadastrar') + ' ' + props.form_name}
        </Titulo>
      </Header>

      <Scroll column scroll-auto>
        {props.fields.map((field) =>
          field.sub_fields ? (
            <field.component key={field.name}>
              {field.sub_fields.map((field_group: FormField) => (
                <field_group.component {...field_group} key={field_group.name} />
              ))}
            </field.component>
          ) : (
            <field.component {...field} key={field.name} />
          )
        )}
      </Scroll>

      {props.loading && <Loading />}

      {props.onCancel && (
        <Button
          type='reset'
          onClick={props.onCancel}
          color={props.is_edit ? theme.colors.error : theme.colors.warning}
          disabled={props.loading}
        >
          {props.is_edit ? 'Cancelar edição' : 'Limpar campos'}
        </Button>
      )}

      <Button color={theme.colors.primary} type='submit' disabled={props.loading}>
        {props.submitText || (props.is_edit ? 'Editar' : 'Cadastrar')}
      </Button>
    </FormContainer>
  );
};

export { Form };
export type { FormField };
